import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { t } from 'ttag';
import config from '../../../../config/channels';
import { operatorName } from '../../../../config/operator';
import { TSite } from '../../../../contexts/site/site-context';
import { validatePostcode } from '../../../../services/api/site';
import { CTA } from '../../../general/Button';
import useOperatorContext from "../../../../contexts/operator/useOperatorContext";
import styled from 'styled-components';
import {iLog} from "../../../../index";
import {setIn} from "formik";
import PageSpinnerThree from "../../../general/PageSpinnerThree";
import {TChannel} from "../../../../contexts/operator/operator-context";
import { trackSiteSelectComplete } from '../../../../services/siteSelectTracking';

type DeliveryTabProps = {
    channel?: TChannel | null;
};

const DeliveryTab = ({ channel }: DeliveryTabProps) => {

    const operatorContext = useOperatorContext();

    const location = useLocation();
    const navigate = useNavigate();

    const [postcode, setPostcode] = useState('');
    const [loading, setLoading] = useState(false);
    const [inlineErrorMessage, setInlineErrorMessage] = useState('');

    const postcodeInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        postcodeInputRef?.current?.focus();
    }, [location.key]);

    const handleDeliveryStart = () => {
        setLoading(true);

        validatePostcode(operatorName, postcode, operatorContext.operator?.flow ?? 'noflow', channel ?? 'delivery')
            .then((sites: TSite[]) => {
                iLog('validatePostcode', [postcode, sites]);

                // The full address is picked later, at checkout
                localStorage.setItem('address', JSON.stringify({ postcode: postcode.toUpperCase() }));

                localStorage.setItem('postcode', postcode.toUpperCase());

                trackSiteSelectComplete('page', channel ?? 'delivery', sites.length === 1 ? sites[0].handle : undefined);

                if ( sites.length === 1 ) {
                    navigate(`/${channel}/${sites[0].handle}`);
                } else {
                    navigate(`/${channel}`);
                }

            })
            .catch((reason: any) => {
                localStorage.removeItem('address');
                localStorage.removeItem('postcode');

                if ( reason.code === 803 ) {
                    setInlineErrorMessage(reason.message);
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handlePostcodeChange = (postcode: string) => {
        setInlineErrorMessage('');
        setPostcode(postcode);
    }

    return (
        <StyledForm>
            <InputWrapper>
                <div className={'postcode-input-wrapper'}>
                    <Form.Control
                        size="lg"
                        type="text"
                        className={'postcode-input'}
                        placeholder={t`Enter the delivery postcode`}
                        value={postcode}
                        onChange={(e) => handlePostcodeChange(e.target.value)}
                        ref={postcodeInputRef}
                    />
                    <span className="material-symbols-outlined postcode-input__search">search</span>
                </div>

                { inlineErrorMessage !== '' && (
                    <ErrorContainer>
                        { inlineErrorMessage }
                    </ErrorContainer>
                ) }
            </InputWrapper>
            <CTA
                className={"delivery-cta"}
                block
                disabled={postcode === '' || loading || inlineErrorMessage !== ''}
                loading={loading}
                onClick={handleDeliveryStart}
            >
                <span>{t`Start my order`}</span>
                <StyledSpinnerOverlay show={loading}>
                    <PageSpinnerThree show={true}/>
                </StyledSpinnerOverlay>
            </CTA>
        </StyledForm>
    );
};

const StyledSpinnerOverlay = styled.div<{show: boolean}>`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: #EBEBEB;
    transition: opacity 0.1s ease-in-out;
    opacity: ${(props) => props.show ? '1' : '0'};
    border-radius: var(--radius_l);
`;

const ErrorContainer = styled.div`
    color: var(--invalid);
    padding-top: 8px;
    display: flex;
    align-items: flex-start;
    img { margin-right: 12px; }
    & i { margin-right: 8px; }
`;

const StyledForm = styled(Form.Group)`
    padding-bottom: 50px;
    padding: 0 60px;
    min-height: 400px;
    @media screen and (max-width: 767px) {
        padding: 0 20px;
    }
    .delivery-cta {
      height: 48px;
      padding-top: 18px;
      padding-bottom: 18px;
    }
`;

const InputWrapper = styled.div`
    position: relative;
    margin-bottom: 20px;

    .form-label {
        font-size: 16px;
        @media screen and (min-width: 576px) {
            font-size: var(--core_text_ui_lg_desktop);
        }
        line-height: 24px;
        color: var(--text-black);
        margin-bottom: 8px;
    }

    .postcode-input-wrapper {
        position: relative;
    }

    .postcode-input--active {
        border-color: var(--brand);
    }

    .postcode-input__search {
        position: absolute;
        top: 8px;
        left: 12px;
        padding: 4px;
        border-radius: var(--radius_m);
        color: var(--text-black);
        font-size: 24px;
        line-height: 24px;
    }

    .postcode-input__loading {
        position: relative;
        height: 24px; width: 24px;
    }

    .postcode-input__loading-wrapper {
        position: absolute;
        top: 8px;
        right: 12px;
        padding: 4px;
    }

    .site-filter-input {
        height: 50px;
        &::placeholder { color:var(--placeholder); }
    }

    i.search-icon {
        position: absolute;
        top: calc(24px - 12px); /* 24 is half of height 48) */
        left: 12px;
    }
    i.find-icon {
        position: absolute;
        top: calc(24px - 12px); /* 24 is half of height 48) */
        right: 12px;
        &.with-input { display: none; }
        &:hover { cursor: pointer; }
    }

    .form-control {
        padding: 18px 16px 18px 48px;
        margin-bottom: 0px;

        @media screen and (max-width: 767px) {
            background-position: 17px 12px, right 19px center;
        }
    }

    .location_icon {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: 14px;
        cursor: pointer;
    }

    .spinner-grow {
        height: 18px;
        width: 18px;
    }
`;

export default DeliveryTab;
